/*
 * fyre-pdp-vehicle-compat.js — fetches + renders the Vehicle Compatibility
 * panel client-side instead of server-rendering the full make/model/year
 * list into page HTML. A generic HTTP-only crawler (no JS execution) then
 * sees an empty panel instead of the complete fitment list in the raw
 * response — same intent as fyre-resolve.js's client-side rendering, applied
 * to the non-builder PDP path.
 *
 * Skips entirely on builder umbrellas — fyre-resolve.js already owns
 * [data-pdp-vehicle-compat] there (renderVehicleCompat, rendered from its own
 * live backing graph). Running both would race on the same container.
 *
 * Reads THREE fitment sources so both catalogue models populate (the panel is
 * only rendered by fyre-section-pdp-info.liquid when at least one has data):
 *   1. product.fitment.groups   — auto/PS group metaobjects; `name` = "Make Model Year / ..."
 *   2. variant.fitment.groups   — simple auto pads carry fitment on the variant
 *   3. product.fitment.vehicles — moto/PS vehicle metaobjects (make/model/submodel/year fields)
 *
 * SECOND JOB — the ATC fit VERDICT for vehicle-grain products. When the section
 * sets [data-pdp-fitment][data-has-vehicle-fitment="true"] (product-level
 * fitment.vehicles, empty server group-handles), fyre-pdp.js CANNOT build the
 * synchronous verdict — Liquid caps metaobject-ref expansion at ~50, which floods
 * a 250-vehicle list into a false "does not fit". So this script owns the verdict:
 * it CURSOR-PAGES the full vehicle set (past the 250 Storefront cap), builds a
 * handle Set, and dispatches `fitment:verified` for the selected vehicle. fyre-pdp.js
 * stays fail-closed (fitPending, ATC disabled) until this resolves. This owns the verdict for
 * BOTH vehicle-grain sources: product-level fitment.vehicles (moto/PS) AND product-level
 * fitment.groups (auto discs — T1 fix: group→vehicle refs feed the verdict handle Set; HANDLE-match
 * only for the group path, since the auto (make,model,submodel,year) tuple fallback isn't
 * collision-safe — see collectGroupVehicles). VARIANT-level groups route here too whenever Liquid's
 * 50-item metaobject expansion could have truncated them — server-side a truncated list is
 * indistinguishable from a complete one, so the sync path would deny from a partial set. Variant
 * lists small enough to expand whole keep fyre-pdp.js's synchronous verdict (flag absent).
 */
(function () {
  if (document.querySelector('[data-fyre-resolve]')) return;

  var host = document.querySelector('[data-pdp-vehicle-compat]');
  if (!host) return;

  var productGid = host.dataset.fitmentProductGid;
  var storefrontUrl = host.dataset.storefrontUrl;
  var storefrontToken = host.dataset.storefrontToken;
  if (!productGid || !storefrontUrl || !storefrontToken) return;

  // Verdict ownership — see header. When true we dispatch fitment:verified; when false
  // fyre-pdp.js owns the verdict from server-emitted group handles and we only render the table.
  var fitmentBar = document.querySelector('[data-pdp-fitment]');
  var ownsVerdict = !!(fitmentBar && fitmentBar.getAttribute('data-has-vehicle-fitment') === 'true');
  // Mark that the verdict owner actually loaded — lets fyre-pdp.js's watchdog tell "owner ran but
  // couldn't verify" apart from "owner never loaded" (Product Info section absent / asset 404) and log it.
  if (ownsVerdict) window.__fyreVehicleVerdictOwner = true;

  var PAGE_LIMIT = 20; // safety backstop: 20 × 250 = 5000 vehicles before we stop paging

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  // Dup-handle guard: 617 (make,model,submodel,year) tuples collapse to >=2 metaobject handles; the
  // selector saves the FIRST file-order handle as vehicle.id, but a product may reference a sibling handle,
  // which a raw handle match would read as "does not fit". Match the normalized tuple as a fallback.
  // No differing-vc (genuinely different-fitting) dup tuples exist in Moto/PS, so tuple-match cannot
  // enable a wrong-fit for the vehicle-grain products this gate governs.
  function tupleKey(mk, mdl, sub, yr) {
    return [mk, mdl, sub, yr].map(function (s) {
      return String(s == null ? '' : s).trim().toLowerCase();
    }).join('|');
  }

  function showEmpty() {
    host.innerHTML = '<p class="pdp-info__empty-note">No vehicle compatibility data available for this product.</p>';
  }

  // Collapse a vehicle's individual years into "1999-2002, 2004-2006" style ranges — a non-numeric
  // year (bad data) is kept verbatim and sorted after the numeric ranges rather than dropped.
  function formatYearRanges(years) {
    var nums = [], other = [];
    years.forEach(function (y) {
      var n = parseInt(y, 10);
      if (String(n) === String(y).trim()) nums.push(n); else other.push(String(y).trim());
    });
    nums = Array.from(new Set(nums)).sort(function (a, b) { return a - b; });
    var ranges = [], start = null, prev = null;
    nums.forEach(function (n) {
      if (start === null) { start = n; prev = n; return; }
      if (n === prev + 1) { prev = n; return; }
      ranges.push(start === prev ? String(start) : start + '-' + prev);
      start = n; prev = n;
    });
    if (start !== null) ranges.push(start === prev ? String(start) : start + '-' + prev);
    return ranges.concat(other.sort()).join(', ');
  }

  function gql(query, variables) {
    return fetch(storefrontUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': storefrontToken },
      body: JSON.stringify({ query: query, variables: variables })
    })
      .then(function (r) {
        // WHY: surface transport failures (401 bad token, 429 throttle, 5xx) instead of
        // collapsing them into the same empty panel as a genuinely fitment-less product.
        if (!r.ok) console.warn('[vehicle-compat] Storefront HTTP ' + r.status);
        return r.json();
      })
      .then(function (json) {
        // WHY: a partial GraphQL field error returns data:null + errors[]; log it so a
        // "renders but empty" panel is diagnosable rather than silently degraded.
        if (json && json.errors) console.warn('[vehicle-compat] GraphQL errors', json.errors);
        return json;
      });
  }

  var VEHICLE_FIELDS = [
    'handle',
    'make: field(key: "make") { value }',
    'model: field(key: "model") { value }',
    'submodel: field(key: "submodel") { value }',
    'year: field(key: "year") { value }',
    'display: field(key: "display_name") { value }'
  ].join(' ');

  // Group->vehicle refs feed ONLY the verdict handle Set (collectGroupVehicles reads m.handle and
  // nothing else), so they select `handle` alone. Asking for make/model/submodel/year/display_name here
  // triples the query cost and quadruples the response for data no consumer reads: measured live on
  // sp-906576, requestedQueryCost 10940 -> 3680 and 809KB -> 195KB. The display fields stay on the
  // pVehicles leg, which renderTable does read.
  var GROUP_VEHICLE_FIELDS = 'handle';

  var MAIN_QUERY = [
    'query($id: ID!) {',
    '  product(id: $id) {',
    '    pGroups: metafield(namespace: "fitment", key: "groups") {',
    '      references(first: 250) { pageInfo { hasNextPage } nodes { ... on Metaobject { handle field(key: "name") { value }',
    // Group→vehicle refs — the VERDICT membership for product-level fitment.groups (auto discs).
    '        gv: field(key: "vehicles") { references(first: 250) { pageInfo { hasNextPage } nodes { ... on Metaobject { ' + GROUP_VEHICLE_FIELDS + ' } } } }',
    '      } } }',
    '    }',
    '    pVehicles: metafield(namespace: "fitment", key: "vehicles") {',
    '      references(first: 250) {',
    '        pageInfo { hasNextPage endCursor }',
    '        nodes { ... on Metaobject { ' + VEHICLE_FIELDS + ' } }',
    '      }',
    '    }',
    '    variants(first: 250) {',
    '      nodes { metafield(namespace: "fitment", key: "groups") {',
    '        references(first: 250) { pageInfo { hasNextPage } nodes { ... on Metaobject { handle field(key: "name") { value }',
    // Variant groups carry gv for the same reason product groups do: on simple auto products
    // (service-part discs, pads) the fitment lives on the VARIANT, so this is the verdict membership.
    '          gv: field(key: "vehicles") { references(first: 250) { pageInfo { hasNextPage } nodes { ... on Metaobject { ' + GROUP_VEHICLE_FIELDS + ' } } } }',
    '        } } }',
    '      } }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  var VEHICLE_PAGE_QUERY = [
    'query($id: ID!, $cursor: String!) {',
    '  product(id: $id) {',
    '    pVehicles: metafield(namespace: "fitment", key: "vehicles") {',
    '      references(first: 250, after: $cursor) {',
    '        pageInfo { hasNextPage endCursor }',
    '        nodes { ... on Metaobject { ' + VEHICLE_FIELDS + ' } }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  // A cost-throttled Storefront response arrives as HTTP 200 + errors[] + data:null — NOT a rejection.
  // Treat that (and any missing product/refs) as a hard failure so a partial set is never presented as
  // complete. WHY: an incomplete set silently converts a fitting vehicle into a false "does not fit".
  function isErrorResponse(json) {
    return !json || (json.errors && json.errors.length) || !json.data || !json.data.product;
  }

  // Recursively page the vehicles connection past the 250 cap, accumulating nodes.
  // Resolves { nodes, truncated } — truncated=true means we hit PAGE_LIMIT with more remaining.
  // REJECTS on an error/throttled page so the caller never builds a verdict from a partial set.
  function pageVehicles(cursor, acc, pagesLeft) {
    if (!cursor) return Promise.resolve({ nodes: acc, truncated: false });
    if (pagesLeft <= 0) return Promise.resolve({ nodes: acc, truncated: true });
    return gql(VEHICLE_PAGE_QUERY, { id: productGid, cursor: cursor }).then(function (json) {
      if (isErrorResponse(json)) throw new Error('[vehicle-compat] paged vehicle query error/throttle');
      var refs = json.data.product.pVehicles && json.data.product.pVehicles.references;
      if (!refs) throw new Error('[vehicle-compat] paged vehicle query missing references');
      var nodes = refs.nodes || [];
      Array.prototype.push.apply(acc, nodes);
      var pi = refs.pageInfo;
      if (pi && pi.hasNextPage) {
        // A null/empty endCursor with more pages can't advance → mark truncated (incomplete), never
        // complete — a complete flag would false-deny the unfetched vehicles.
        if (!pi.endCursor) return { nodes: acc, truncated: true };
        return pageVehicles(pi.endCursor, acc, pagesLeft - 1);
      }
      return { nodes: acc, truncated: false };
    });
  }

  // Build the ATC verdict membership from group→vehicle references. renderTable (display) parses
  // group NAME strings; the VERDICT needs real vehicle handles to match the selected vehicle's id.
  // Only groups-grain products (auto discs) reach this with populated gv — moto/PS carry fitment.vehicles
  // and pGroups has no gv, so this is a no-op for them (union stays correct either way).
  //
  // HANDLE-MATCH ONLY — deliberately NOT the tuple fallback (handleSet, not tupleSet). The tupleKey
  // fallback (make,model,submodel,year) is collision-safe ONLY for Moto/PS (see tupleKey
  // note). For AUTO, vehicle qualifiers (PCCB, chassis code, xDrive, M-Sport) are LOAD-BEARING
  // [[feedback_vehicle_qualifiers_load_bearing]] — two differently-fitting vehicles can share that
  // 4-field tuple, so a tuple fallback here could false-ALLOW a wrong-fit brake purchase. Auto fitment
  // is verified by exact handle (fyre-pdp.js ownFitHandles/fitHandles), so this matches
  // that contract. Trade-off: if a group ever references a non-canonical sibling dup-handle, that
  // vehicle false-DENIES (safe direction — lost sale, never wrong-fit) — flag for smoke.
  //
  // Returns truncated=true if the groups page OR any group's vehicles page was cut off — fail-closed:
  // an incomplete set must never DENY a possibly-fitting vehicle (propagates into setComplete).
  function collectGroupVehicles(pGroups, set) {
    var refs = pGroups && pGroups.references;
    if (!refs) return false;
    var truncated = !!(refs.pageInfo && refs.pageInfo.hasNextPage);
    (refs.nodes || []).forEach(function (g) {
      var gv = g && g.gv && g.gv.references;
      if (!gv) return;
      if (gv.pageInfo && gv.pageInfo.hasNextPage) truncated = true;
      (gv.nodes || []).forEach(function (m) {
        if (m && m.handle) set[m.handle] = true;
      });
    });
    return truncated;
  }

  function collectGroups(refNodes, seen, groupNames) {
    (refNodes || []).forEach(function (g) {
      if (g && g.handle && !seen[g.handle]) {
        seen[g.handle] = true;
        var name = g.field && g.field.value;
        if (name) groupNames.push(name);
      }
    });
  }

  // ── Verdict wiring (only when ownsVerdict) ──────────────────────────────────
  // fetchState: 'loading' (in flight → ATC stays fitPending) | 'ready' (set built) | 'failed'
  // (errored after retries → honest verify-unavailable, NEVER a false no-fit).
  var handleSet = null;
  var tupleSet = null;       // normalized-tuple fallback so a sibling dup-handle still verifies
  var setComplete = false;   // true only after a CLEAN, non-truncated full fetch — REQUIRED to deny fit
  var fetchState = 'loading';
  var pendingVehicle = null;
  try {
    pendingVehicle = window.FitmentStorage ? window.FitmentStorage.get()
      : JSON.parse(localStorage.getItem('fitment_vehicle') || 'null');
  } catch (e) { pendingVehicle = null; }

  function emitVerdict(fits) {
    // Mirror onto the bar's visual. Deferred (below), so applyVehicle's synchronous class-reset can't clobber it.
    if (fitmentBar) {
      fitmentBar.classList.toggle('pdp-fitment--confirmed', fits);
      fitmentBar.classList.toggle('pdp-fitment--no-fit', !fits);
    }
    window.dispatchEvent(new CustomEvent('fitment:verified', { detail: { fits: fits } }));
  }

  // Resolve the ATC verdict for the selected vehicle. Deferred one tick so it lands AFTER FyreATC's
  // synchronous fitment:changed pending-reset (listener-order race). SAFETY INVARIANT: never assert
  // fits:false from an incomplete or failed fetch — a fitting customer must not be told "does not fit"
  // because of a throttle/partial. Only a clean, COMPLETE set can deny; otherwise emit
  // fitment:verify-unavailable so the ATC shows an honest, retryable state (fyre-pdp.js).
  function dispatchVerdict(vehicle) {
    if (!vehicle || !vehicle.id) return;
    if (fetchState === 'loading') return; // still in flight → stay pending; resolved on completion
    setTimeout(function () {
      if (vehicle !== pendingVehicle) return; // a newer selection superseded this → drop the stale verdict
      // Match the collapsed handle OR the normalized tuple (dup-handle safe — see tupleKey).
      var inSet = (handleSet && handleSet[vehicle.id] === true) ||
                  (tupleSet && tupleSet[tupleKey(vehicle.make, vehicle.model, vehicle.submodel, vehicle.year)] === true);
      if (inSet) { emitVerdict(true); return; }                                   // in set → fits (safe even if partial)
      if (fetchState === 'ready' && setComplete) { emitVerdict(false); return; }  // complete + absent → no-fit
      window.dispatchEvent(new CustomEvent('fitment:verify-unavailable'));        // unknown → honest, no false deny
    }, 0);
  }

  if (ownsVerdict) {
    window.addEventListener('fitment:changed', function (e) {
      pendingVehicle = e.detail || null;
      if (pendingVehicle) dispatchVerdict(pendingVehicle); // no-op while loading; resolves once ready/failed
    });
  }

  // One load attempt: main query + full cursor-paging. Rejects on any error/throttled response.
  function loadOnce() {
    return gql(MAIN_QUERY, { id: productGid }).then(function (json) {
      if (isErrorResponse(json)) throw new Error('[vehicle-compat] main query error/throttle');
      var product = json.data.product || {};
      var pVehRefs = product.pVehicles && product.pVehicles.references;
      var firstNodes = (pVehRefs && pVehRefs.nodes) || [];
      var pi = pVehRefs && pVehRefs.pageInfo;
      var pager;
      if (pi && pi.hasNextPage) {
        // hasNextPage with no cursor → an incomplete set we can't page → truncated, never complete.
        pager = pi.endCursor
          ? pageVehicles(pi.endCursor, firstNodes.slice(), PAGE_LIMIT)
          : Promise.resolve({ nodes: firstNodes, truncated: true });
      } else {
        pager = Promise.resolve({ nodes: firstNodes, truncated: false });
      }
      return pager.then(function (res) { return { product: product, vehNodes: res.nodes, truncated: res.truncated }; });
    });
  }

  // Retry transient failures (429/5xx/network) with backoff before giving up — a blip must not
  // strand a brake product as unbuyable if a retry would resolve it.
  function attemptLoad(retriesLeft, delay) {
    return loadOnce().catch(function (err) {
      if (retriesLeft <= 0) throw err;
      return new Promise(function (resolve) { setTimeout(resolve, delay); })
        .then(function () { return attemptLoad(retriesLeft - 1, delay * 2); });
    });
  }

  function renderTable(product, vehNodes, truncated) {
    var groupSeen = {}, groupNames = [];
    collectGroups(product.pGroups && product.pGroups.references && product.pGroups.references.nodes, groupSeen, groupNames);
    var variants = (product.variants && product.variants.nodes) || [];
    variants.forEach(function (v) {
      collectGroups(v.metafield && v.metafield.references && v.metafield.references.nodes, groupSeen, groupNames);
    });

    // Rows are ONE PER GROUP for the group-grain model (auto): the group name is already a curated
    // model-generation label carrying its year range, so render it verbatim instead of exploding it into
    // per-vehicle rows. The vehicle-grain model (moto/PS
    // fitment.vehicles — no groups) has no such label, so those individual vehicles are still condensed to
    // one row per make/model with a collapsed year range. Single column, deduped, sorted so makes cluster.
    var rowSeen = {}, lines = [];
    function addLine(s) { s = String(s == null ? '' : s).trim(); if (!s || rowSeen[s]) return; rowSeen[s] = true; lines.push(s); }

    // GROUP rows — verbatim name; skip non-vehicle names (raw SKU-list groups) by requiring a 4-digit year.
    groupNames.forEach(function (gname) {
      gname = String(gname == null ? '' : gname).trim();
      // Accept 4-digit years AND JDM/curated 2-digit forms ("99-01", "20+", "23+") — real vehicle labels
      // (Skyline R34, Alphard, Harrier); only SKU-placeholder group names (no year token) are dropped.
      if (gname && /(?:19|20)\d{2}|\b\d{2}[-–]\d{2}\b|\b\d{2}\+/.test(gname)) addLine(gname);
    });

    // VEHICLE rows (moto/PS) — aggregate individual vehicle metaobjects to "Make Model Submodel <year-range>".
    var vehMap = {}, vehOrder = [];
    vehNodes.forEach(function (m) {
      if (!m) return;
      // WHY: recover name and year INDEPENDENTLY from display_name ("Year Make Model") — a
      // vehicle with make/model but a blank `year` field must not be dropped, and vice-versa.
      var year = (m.year && m.year.value) || '';
      var name = [m.make && m.make.value, m.model && m.model.value, m.submodel && m.submodel.value]
        .filter(Boolean).join(' ').trim();
      if ((!name || !year) && m.display && m.display.value) {
        var dp = String(m.display.value).trim().split(' '), leadYear = /^(?:19|20)\d{2}$/.test(dp[0]);
        if (!year && leadYear) year = dp[0];
        if (!name) name = leadYear ? dp.slice(1).join(' ').trim() : dp.join(' ');
      }
      if (!name) return;
      if (!vehMap[name]) { vehMap[name] = []; vehOrder.push(name); }
      if (year) vehMap[name].push(year);
    });
    vehOrder.forEach(function (name) {
      var yr = formatYearRanges(vehMap[name]);
      addLine(yr ? name + ' ' + yr : name);
    });

    lines.sort(function (a, b) { return a.localeCompare(b); });

    var rows = '';
    lines.forEach(function (s) { rows += '<tr><td>' + esc(s) + '</td></tr>'; });

    if (!rows) { showEmpty(); return; }
    // Only keep the note when the set is TRUNCATED — that is a real data-completeness warning (the list is
    // incomplete).
    var noteHtml = truncated
      ? '<p class="pdp-info__compat-note">Showing the first ' + lines.length + ' entries; some vehicles are not listed.</p>'
      : '';
    host.innerHTML = '<table class="pdp-info__compat-table pdp-info__compat-table--groups"><tbody>' + rows +
      '</tbody></table>' + noteHtml;
  }

  attemptLoad(2, 600)
    .then(function (ctx) {
      var set = {}, tset = {};
      ctx.vehNodes.forEach(function (m) {
        if (!m) return;
        if (m.handle) set[m.handle] = true;
        // Also key the normalized tuple so a product referencing a sibling dup-handle still verifies.
        tset[tupleKey(m.make && m.make.value, m.model && m.model.value, m.submodel && m.submodel.value, m.year && m.year.value)] = true;
      });
      // Fold product-level group→vehicle HANDLES into handleSet — the verdict source for auto discs
      // (pVehicles is empty for them). Handle-only by design (see collectGroupVehicles): the auto tuple
      // is not collision-safe. No-op for moto/PS (pGroups carries no gv), so the union stays correct.
      var groupsTruncated = collectGroupVehicles(ctx.product && ctx.product.pGroups, set);
      // VARIANT-level groups are the verdict source for simple auto products (service-part discs, pads).
      // Liquid's 50-item metaobject cap silently truncates that list, so the synchronous path can DENY a
      // vehicle whose group sits past the cap; this paged read is the only complete view of it.
      ((ctx.product && ctx.product.variants && ctx.product.variants.nodes) || []).forEach(function (v) {
        if (collectGroupVehicles(v && v.metafield, set)) groupsTruncated = true;
      });
      handleSet = set;
      tupleSet = tset;
      // A truncated vehicle page OR an unfetched group / group-vehicles page may miss a fitting vehicle
      // → cannot DENY (only confirm). Both feed setComplete so the deny path stays fail-closed.
      setComplete = !ctx.truncated && !groupsTruncated;
      fetchState = 'ready';
      if (ownsVerdict && pendingVehicle) dispatchVerdict(pendingVehicle);
      renderTable(ctx.product, ctx.vehNodes, ctx.truncated);
    })
    .catch(function (err) {
      if (err) console.warn(err.message || err);
      fetchState = 'failed';
      showEmpty();
      // Resolve any already-selected vehicle to the honest verify-unavailable state — never a false no-fit.
      if (ownsVerdict && pendingVehicle) dispatchVerdict(pendingVehicle);
    });
})();
